import {
  alpha,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
  createSvgIcon,
  TextField,
  InputAdornment,
  Autocomplete,
  Grid2,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useNotification } from "../../../componentes/notificationProvider";
import api from "../../../services/api";
import { ptBR } from "@mui/x-data-grid/locales";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";
import { ReactComponent as IconReport } from "../../../assets/icons/IconEasyReport.svg";
import { ReactComponent as IconMaps } from "../../../assets/icons/IconEasyMaps.svg";
import { ReactComponent as IconLog } from "../../../assets/icons/IconEasyLog.svg";
import {
  Clear,
  Close,
  FilterAltOutlined,
  OpenInNew,
  Search,
} from "@mui/icons-material";
import DetailsModal from "./modalDetails";
import DateFilter from "./dateFilter";
import useStyles from "./styles";

const EasyReportIcon = createSvgIcon(<IconReport />);
const EasyMapsIcon = createSvgIcon(<IconMaps />);
const EasyLogIcon = createSvgIcon(<IconLog />);

const modules = [
  {
    name: "EasyMaps",
    icon: EasyMapsIcon,
    color: "#E94159",
  },
  {
    name: "EasyReport",
    icon: EasyReportIcon,
    color: "#F48206",
  },
  {
    name: "EasyLogs",
    icon: EasyLogIcon,
    color: "#E93136",
  },
];

export const renderActionLabel = (action) => {
  switch (action) {
    case 0:
      return "Criação";
    case 1:
      return "Edição";
    case 2:
      return "Exclusão";
    case 3:
      return "Upsert";
    default:
      return "-";
  }
};
export default function Auditoria() {
  const { setSubTitle } = useOutletContext();
  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const notify = useNotification();
  const styles = useStyles(theme, colorMode);
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [module, setModule] = useState(null);
  const [filtersState, setFilterState] = useState({
    timeStart: new Date(new Date().setHours(new Date().getHours() - 24)),
    timeEnd: new Date(),
    option: null,
  });

  const dateFilterRef = useRef();
  useEffect(() => {
    setSubTitle("Auditoria");
  }, []);

  useEffect(() => {
    setSelectedRow((row) => row?.id && rows.find((r) => r.id === row.id));
  }, [rows]);

  useEffect(() => {
    async function apiget(params) {
      try {
        setLoadingPage(true);
        const response = await api.post("/findManyLogs", {
          initialDate: filtersState.timeStart,
          finalDate: filtersState.timeEnd,
          option: filtersState.option,
        });

        setRows(response.data);
      } catch (error) {
        console.error(error);
        if (error?.response?.data?.error)
          notify(error.response.data.error, "error");
        else notify("Não foi possível se conectar ao servidor!", "error");
      } finally {
        setLoadingPage(false);
      }
    }
    apiget();
  }, [filtersState.timeStart, filtersState.timeEnd, filtersState.option]);

  const handleClick = (event, row) => {
    setSelectedRow(row);
    setOpenDetailsModal(true);
  };
  const handleSearchInputChange = (event) => {
    setSearchText(event.target.value);
  };
  const handleCleanFilters = () => {
    setModule(null);
    setFilterState({
      timeStart: new Date(new Date().setHours(new Date().getHours() - 24)),
      timeEnd: new Date(),
      option: null,
    });
  };
  const getFilteredRows = useMemo(() => {
    return rows?.filter((row) => {
      const matchesSearch = searchText
        ? row?.user?.profile?.name
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          row?.user?.login?.toLowerCase().includes(searchText.toLowerCase()) ||
          row?.resource?.toLowerCase().includes(searchText.toLowerCase())
        : true;
      const matchesModule = module
        ? row?.module?.toLowerCase() === module.name.toLowerCase()
        : true;
      return matchesSearch && matchesModule;
    });
  }, [rows, searchText, module]);

  const columns = [
    {
      flex: 1,
      minWidth: 120,
      field: "date",
      headerName: "Hora",
      renderCell: (params) => {
        return (
          params.value && (
            <Stack sx={styles.containerColumnCreatedAt} spacing={1}>
              <Typography fontSize={13} sx={styles.textDate}>
                {dayjs(params.value)
                  .locale("pt-br")
                  .format("DD/MM/YYYY [às] HH:mm:ss")}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      flex: 1,
      minWidth: 120,
      field: "name",
      headerName: "Usuário",
      valueGetter: (value, row) =>
        row?.user?.profile?.name || row?.user?.login || "-",
    },
    {
      width: 140,
      field: "action",
      headerName: "Ação",
      renderCell: (params) => {
        return (
          params.value != null && (
            <Stack sx={styles.containerColumnPermissao} spacing={1}>
              <Chip
                key={params?.value}
                label={renderActionLabel(params?.value)}
                sx={
                  params?.value === 0
                    ? styles.chipActionCreate
                    : params?.value === 1
                      ? styles.chipActionEdit
                      : params?.value === 2
                        ? styles.chipActionDelete
                        : styles.chipActionUpsert
                }
                variant="contained"
              />
            </Stack>
          )
        );
      },
    },
    {
      flex: 1,
      minWidth: 140,
      field: "resource",
      headerName: "Recurso",
      renderCell: (params) => {
        return (
          params.value && (
            <Stack sx={styles.containerColumnCreatedAt} spacing={1}>
              <Typography fontSize={13} sx={{ textTransform: "capitalize" }}>
                {params.value}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      flex: 1,
      minWidth: 140,
      field: "module",
      headerName: "Módulo",
      renderCell: (params) => {
        const mod = modules.find(
          (m) => m.name.toLowerCase() === params.value?.toLowerCase(),
        );
        const Icon = mod?.icon;
        return (
          params.value && (
            <Stack direction="row" alignItems="center" gap={0.5} height="100%">
              {Icon && <Icon sx={{ fontSize: 16, color: mod.color }} />}
              <Typography
                fontSize={13}
                sx={{
                  color: mod?.color ?? "text.primary",
                  textTransform: "capitalize",
                  fontWeight: 500,
                }}
              >
                {params.value}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      width: 160,
      field: "ip",
      headerName: "IP",
      valueGetter: (value, row) => row?.session?.ip || "-",
    },
    {
      width: 80,
      field: "details",
      headerName: "Detalhes",
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(e) => handleClick(e, params.row)}
            fontSize="large"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">Nenhum log encontrado</Typography>
      </Box>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={styles.paper}>
        <Box sx={styles.boxSearchAndFilter}>
          <TextField
            size="small"
            id="outlined-adornment-amount"
            sx={styles.searchInput}
            value={searchText}
            onChange={handleSearchInputChange}
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchText("")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            placeholder="Pesquisar por usuário e recurso"
          />
          <Box sx={styles.backgroundBox}>
            <Grid2 container rowSpacing={1} spacing={1} width={"100%"}>
              <Grid2 item size={styles.sizeGridItem}>
                <Box sx={styles.boxTitle}>
                  <FilterAltOutlined sx={styles.size} />
                  <Typography sx={styles.text}>{"Filtros:"}</Typography>
                </Box>
              </Grid2>

              <Grid2 item size={styles.sizeGridItem}>
                <Autocomplete
                  sx={{
                    width: 270,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: theme.palette.background.paper,
                      ":hover": {
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.5,
                        ),
                      },
                    },
                  }}
                  value={module}
                  options={modules}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  onChange={(event, newValue) => {
                    setModule(newValue);
                  }}
                  renderInput={(params) => {
                    const Icon = module?.icon;
                    return (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Filtrar por módulo"
                        sx={{
                          width: 270,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.background.paper,
                            ":hover": {
                              backgroundColor: alpha(
                                theme.palette.background.paper,
                                0.5,
                              ),
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: Icon ? (
                              <>
                                <Icon
                                  sx={{ fontSize: 20, color: module.color }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ) : (
                              params.InputProps.startAdornment
                            ),
                          },
                        }}
                      />
                    );
                  }}
                  renderOption={(props, option) => {
                    const Icon = option.icon;
                    return (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          py: 1,
                        }}
                      >
                        <Icon sx={{ fontSize: 20, color: option.color }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    );
                  }}
                />
              </Grid2>
              <Grid2 item size={styles.sizeGridItem}>
                <DateFilter
                  ref={dateFilterRef}
                  key={"modalDateFilterAuditoria"}
                  filtersState={filtersState}
                  setFilterState={setFilterState}
                />
              </Grid2>

              {filtersState.option || module ? (
                <Grid2 item size={styles.sizeGridItem}>
                  <Box sx={styles.boxTitle}>
                    <Button
                      sx={styles.buttonClearFilters}
                      startIcon={<Close />}
                      onClick={handleCleanFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </Box>
                </Grid2>
              ) : null}
            </Grid2>
          </Box>
        </Box>

        <DataGrid
          sx={styles.dataGrid}
          rows={getFilteredRows}
          columns={columns}
          loading={loadingPage}
          disableColumnMenu={true}
          pageSizeOptions={[10, 25, 50, 100]}
          onPaginationModelChange={(model) => setPageSize(model.pageSize)}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          getRowClassName={(params) => {
            const isEven =
              params.indexRelativeToCurrentPage % 2 === 0
                ? "even-row"
                : "odd-row";

            const isPending =
              params.row.recoveryDate === null ? "blink-row" : "";

            return `${isEven} ${isPending}`;
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: pageSize },
            },
          }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>{" "}
      <DetailsModal
        openDetailsModal={openDetailsModal}
        setOpenDetailsModal={setOpenDetailsModal}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
      />
    </>
  );
}
